package com.crowpay.views.components.project.activity.concerns

import com.crowpay.AccessAndFeedback
import com.crowpay.AccessInfo
import com.crowpay.ChangeRequestItem
import com.crowpay.DisplayableState
import com.crowpay.FlowMessage
import com.crowpay.HasDisplayName
import com.crowpay.LineItemCompletionMessage
import com.crowpay.LineItemInterface
import com.crowpay.LineItemState
import com.crowpay.PayAppItem
import com.crowpay.PayApplication
import com.crowpay.PendingItemChange
import com.crowpay.PendingLineItem
import com.crowpay.completionResponse.CompletionResponse
import com.crowpay.utils.AdjustedLineItem
import com.crowpay.utils.renderDollars
import com.crowpay.views.components.project.work.changeOrders.ChangeRequestItemWrapper
import com.crowpay.views.components.project.work.payApps.DirectlyDisplayableState
import com.crowpay.views.dialogs.ChangesRejectedDetails
import com.crowpay.views.dialogs.DrawWaitedDetails
import com.crowpay.views.screens.common.ActionImportance
import com.crowpay.views.screens.common.DialogOrPage
import com.crowpay.views.theming.AppColors
import com.lightningkite.UUID
import com.lightningkite.kiteui.models.Color
import com.lightningkite.kiteui.navigation.Page
import com.lightningkite.now
import kotlinx.datetime.Instant

data class ConcernCardInfo(
    val type: Type,
    val id: UUID,
    val title: String,
    val state: DisplayableState,
    val timestamp: Instant,
    val mostRecentMessage: FlowMessage,
    val readBy: Set<UUID> = emptySet(),
    val actionIcon: ActionImportance? = null,
    val dialog: (suspend ()-> DialogOrPage)? = null
): Comparable<ConcernCardInfo> {
    enum class Type(val displayName: String) { LineItem("Line Item"), Incident("Incident"), Draw("Draw"), Change("Change") }

    data class ManualState(
        override val displayName: String,
        override val color: Color,
        override val priority: Int = 0
    ) : DirectlyDisplayableState

    override fun compareTo(other: ConcernCardInfo): Int = comparator.compare(this, other)

    constructor(draw: PayAppItem, lineItem: LineItemInterface, payApp: PayApplication, multiple: Boolean = false, dialog: suspend () -> DrawWaitedDetails) : this(
        type = Type.Draw,
        id = draw._id,
        title = "Draw ${payApp.number}",
        state = ManualState("Waited", AppColors.red.main),
        timestamp = draw.reviewedOrSubmitted ?: now(),
        mostRecentMessage = MessageData(draw, lineItem, payApp.number, multiple),
        dialog = dialog,
        readBy = draw.denied?.readBy ?: setOf()
    )

    constructor(item: ChangeRequestItem, multiple: Boolean = false, dialog: suspend () -> ChangesRejectedDetails): this(
        type = Type.Change,
        id = item._id,
        title = "Change ${item.itemNumber.substringBefore('.')}",
        state = ManualState("Rejected", AppColors.red.main),
        timestamp = item.denied()?.timestamp ?: item.accepted()?.timestamp ?: now(),
        mostRecentMessage = MessageData(item, multiple),
        dialog = dialog,
        readBy = item.denied()?.readBy ?: setOf()
    )

    companion object {
        val comparator = compareBy<ConcernCardInfo> { it.state.priority }.thenBy { it.timestamp }
    }
}

private fun ChangeRequestItem.denied(): AccessAndFeedback? = when (this) {
    is PendingItemChange -> this.denied
    is PendingLineItem -> this.denied
    is ChangeRequestItemWrapper -> this.wrapped.denied()
    else -> throw IllegalArgumentException("Unsupported ChangeRequestItem")
}
private fun ChangeRequestItem.accepted(): AccessInfo? = when (this) {
    is PendingItemChange -> this.accepted
    is PendingLineItem -> this.accepted
    is ChangeRequestItemWrapper -> this.wrapped.accepted()
    else -> throw IllegalArgumentException("Unsupported ChangeRequestItem")
}

data class MessageData(
    override val message: String,
    override val clientMessage: Boolean,
    override val created: Instant,
    override val sender: UUID,
    override val type: HasDisplayName,
    override val readBy: Set<UUID> = emptySet()
) : FlowMessage {

    constructor(draw: PayAppItem, lineItem: LineItemInterface, number: Int, multiple: Boolean = false) : this(
        message = draw.denied?.feedback ?: "",
        clientMessage = true,
        created = draw.reviewedOrSubmitted ?: now(),
        sender = draw.denied?.access?.user ?: draw.approved?.user ?: UUID.random(),
        type = HasDisplayName.Manual("${lineItem.name}, ${draw.amount.renderDollars()}" + if (multiple) " (Multiple Waits)" else ""),
        readBy = draw.denied?.readBy ?: setOf()
    )
    constructor(item: ChangeRequestItem, multiple: Boolean = false) : this(
        message = item.denied()?.feedback ?: "",
        clientMessage = true,
        created = item.denied()?.timestamp ?: item.changeAccepted ?: now(),
        sender = item.denied()?.access?.user ?: item.accepted()?.user ?: UUID.random(),
        type = HasDisplayName.Manual("Change ${item.itemNumber}" + if (multiple) " (Multiple Rejections)" else ""),
        readBy = item.denied()?.readBy ?: setOf()
    )
}

fun LineItemState.showsCard(): Boolean = this in setOf(
    LineItemState.OnHold,
    LineItemState.NotApproved,
    LineItemState.KeepWorking,
    LineItemState.ForceComplete,
    LineItemState.Issue,
    LineItemState.Disputed,
    LineItemState.Resolving,
    LineItemState.Complete,
    LineItemState.RequestingReview
)

fun AdjustedLineItem.hasConcernCard(history: List<LineItemCompletionMessage>): Boolean =
    state.showsCard() and history.any {
        it.responseType.let { it !is CompletionResponse.RequestComplete && it !is CompletionResponse.Accept }
    }
